// This needs to be client side rendered while we're still using the redux persist package
// TODO: Refactor after we've moved to a more sensible strategy for persisting basket state during onboarding

'use client'

import React, { ReactElement, ReactNode, useEffect } from 'react'
import { Box, Container, GlobalStyles } from '@mindfulchefuk/design-system'
import { OnboardingLayoutGrid } from '@mindfulchefuk/features/Onboarding/components/layout/OnboardingLayout.styles'
import MainLayout from '@mindfulchefuk/layouts/MainLayout'
import { useCartDrawer } from '@mindfulchefuk/features/Cart/useCartDrawer'
import { DeprecatedOnboardingCartDrawer } from '@mindfulchefuk/onboarding/components/OnboardingCartDrawer/DeprecatedOnboardingCartDrawer'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '@mindfulchefuk/types/store'
import { useGroupRecipesByQuantity } from '@mindfulchefuk/features/Onboarding/hooks/useGroupRecipesByQuantity'
import { useRecipeCartTotals } from '@mindfulchefuk/features/Onboarding/hooks/useRecipeCartTotals'
import { useDeprecatedChooseRecipesActions } from '@mindfulchefuk/features/Onboarding/hooks/useDeprecatedChooseRecipeActions'
import { DeprecatedBasketBanner } from '@mindfulchefuk/features/Onboarding/components/ChooseRecipes/DeprecatedBasketBanner'
import { DeprecatedOnboardingCartNavigation } from '@mindfulchefuk/features/Cart/components/CartNavigation/Onboarding/DeprecatedOnboardingCartNavigation'

import { DeprecatedNotificationSuccessVoucherApplied } from '@mindfulchefuk/features/Notifications/components/variants/DeprecatedNotificationSuccessVoucherApplied'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import { getMinimumMaximumRecipesForPortion } from '@mindfulchefuk/services/getMinimumMaximumRecipesForPortion'

type TChooseRecipesLayout = {
  children: ReactNode
  title?: string
  canonical?: string
  description?: string
}

export const DeprecatedChooseRecipesLayout = ({
  canonical,
  title,
  description,
  children,
}: TChooseRecipesLayout): ReactElement => {
  const {
    items,
    portionCount,
    discountApplied,
    total,
    totalWithDiscount,
    isBasketLoading,
    shippingPrice,
  } = useSelector(
    (store: RootState) => ({
      items: store.basket.items,
      portionCount: store.basket.portionCount,
      discountApplied: store.basket.discountApplied,
      total: store.basket.total,
      totalWithDiscount: store.basket.totalWithDiscount,
      isBasketLoading: store.basket.isBasketLoading,
      shippingPrice: store.basket.recipeShippingPrice,
    }),
    shallowEqual
  )
  const { total: finalTotal, originalTotal } = useRecipeCartTotals()
  const recipesByQuantity = useGroupRecipesByQuantity(items)

  const {
    handleConfirmClick,
    handleIncrementRecipeFromBasket,
    handleDecrementRecipeFromBasket,
    handleConfirmClickFromTheBasket,
  } = useDeprecatedChooseRecipesActions()

  const { minimumRecipes: minimumRecipesForPortion } =
    getMinimumMaximumRecipesForPortion(portionCount)

  const minRecipesNotReached = items.length < minimumRecipesForPortion

  const { isDrawerOpen, toggleDrawerOpen } = useCartDrawer()

  useEffect(() => {
    if (isDrawerOpen) {
      trackAnalyticsEvent({
        name: 'basket viewed',
        data: {
          'product types in basket': 'recipe',
          'delivery amount': shippingPrice,
          'total recipe amount': finalTotal,
          'total ready to go amount': 0,
          'total basket amount': finalTotal,
          'total discount amount': undefined,
          'promo codes applied': undefined,
          'cart id': '',
          'cart size': items.length,
          'page source': 'choose recipe page',
          'user is logged in': false,
          rawPageSource: sessionStorage.getItem('referrerPath'),
        },
      })
    }
  }, [isDrawerOpen, items.length, shippingPrice, finalTotal])

  return (
    <MainLayout
      hideFooter
      hideNav
      title={title}
      description={description}
      canonical={canonical}
      isOnboarding
      customNav={
        <Box ml="auto">
          <DeprecatedOnboardingCartNavigation
            toggleDrawerOpen={toggleDrawerOpen}
          />
        </Box>
      }
    >
      <DeprecatedNotificationSuccessVoucherApplied />
      <GlobalStyles />
      <Box overflow="hidden">
        <Container>
          <OnboardingLayoutGrid>{children}</OnboardingLayoutGrid>
        </Container>
      </Box>
      <DeprecatedBasketBanner
        numRecipes={items.length}
        price={total}
        portionCount={portionCount}
        discountApplied={discountApplied}
        totalWithDiscount={totalWithDiscount}
        isBasketLoading={isBasketLoading}
        onPressContinue={handleConfirmClick}
        toggleDrawerOpen={toggleDrawerOpen}
      />

      <DeprecatedOnboardingCartDrawer
        isDrawerOpen={isDrawerOpen}
        toggleDrawerOpen={toggleDrawerOpen}
        onPressContinue={
          minRecipesNotReached
            ? toggleDrawerOpen
            : handleConfirmClickFromTheBasket
        }
        total={finalTotal}
        originalTotal={originalTotal}
        recipes={recipesByQuantity}
        addRecipe={handleIncrementRecipeFromBasket}
        removeRecipe={handleDecrementRecipeFromBasket}
        orderShippingAmount={shippingPrice}
      />
    </MainLayout>
  )
}
